import { useEffect, useState } from 'react';

import { themeEventEmitter } from '@shared/utils/event-bus/event-bus.util';

import type { IThemeContextValue } from './context';
import type { IThemeProviderProps, TTheme } from '@app-types/theme';

import { ThemeContext } from './context';

export const ThemeProvider: React.FC<IThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  storageKey = 'ui-theme',
}) => {
  const [theme, setThemeState] = useState<TTheme>(() => {
    // Try to get from localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    }
    return defaultTheme;
  });

  const [systemTheme, setSystemTheme] = useState<TTheme>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove previous theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(theme);

    // Create or update the theme CSS style element
    // NOTE: panel.css handles all token values via :root and .dark/.light selectors.
    // The ThemeProvider only needs to toggle the class — no JS token injection needed.
    let themeStyle = document.getElementById('theme-variables');
    if (!themeStyle) {
      themeStyle = document.createElement('style');
      themeStyle.id = 'theme-variables';
      document.head.appendChild(themeStyle);
    }
    themeStyle.textContent = '/* panel.css handles all tokens */';

    // Store in localStorage
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  const setTheme = (newTheme: TTheme) => {
    const previousTheme = theme;
    setThemeState(newTheme);

    // Emit theme change event
    if (previousTheme !== newTheme) {
      themeEventEmitter.emit({
        theme: newTheme,
        source: 'ThemeProvider',
      });
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const value: IThemeContextValue = {
    theme,
    setTheme,
    toggleTheme,
    systemTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
